import {
	BoldOutlined,
	ItalicOutlined,
	LinkOutlined,
	PaperClipOutlined,
	UnderlineOutlined,
} from "@ant-design/icons";
import {
	ChangeEvent,
	ClipboardEvent,
	DragEvent,
	MouseEvent,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import styled from "styled-components";
import { axiosInstance } from "../../utils/axiosInstance";
import { NEWS_API_ENDPOINTS } from "../../context/NewsContext/constants";
import { countText, LineBreakCount } from "../../utils/textLength";

interface RichTextComponentProps {
	messageTemplate?: string;
	handleSetChangedTemplate: (body: string) => void;
	placeholder?: string;
	backgroundColor?: string;
	height?: string | number;
	resizable?: boolean;
	// Shows "count / max" under the editor. The count is the length of the HTML the form keeps, formatting included,
	// because that is what the backend checks; the editor turns red above max (the form's validator blocks the save).
	maxLength?: number;
	// "crlf" when the value is sent as multipart form data: the backend gets every line break as \r\n (two characters).
	lineBreaks?: LineBreakCount;
}

type FormatCommand = "bold" | "italic" | "underline";

export const TEXT_EDITOR_HEIGHT = 450;

const getCssSize = (value: string | number) =>
	typeof value === "number" ? `${value}px` : value;

const EditorContainer = styled.div<{
	$backgroundColor?: string;
	$height: string | number;
	$resizable: boolean;
	$exceeded?: boolean;
}>`
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	height: ${(props) => getCssSize(props.$height)};
	min-height: ${(props) => getCssSize(props.$height)};
	background-color: ${(props) => props.$backgroundColor || "#424242"};
	border: 1px solid ${(props) => (props.$exceeded ? "#ff4d4f" : "#d9d9d9")};
	border-radius: 6px;
	overflow: hidden;
	resize: ${(props) => (props.$resizable ? "vertical" : "none")};
	transition:
		border-color 0.2s,
		box-shadow 0.2s;

	&:focus-within {
		border-color: ${(props) => (props.$exceeded ? "#ff4d4f" : "#4096ff")};
		box-shadow: 0 0 0 2px ${(props) => (props.$exceeded ? "rgb(255 38 5 / 6%)" : "rgb(5 145 255 / 10%)")};
	}
`;

// Placed like the count of antd's text area: under the box, on the right, in the same grey and red.
const CharacterCount = styled.div<{ $exceeded: boolean }>`
	margin-top: 4px;
	font-size: 14px;
	line-height: 22px;
	text-align: right;
	color: ${(props) => (props.$exceeded ? "#ff4d4f" : "rgba(0, 0, 0, 0.45)")};
`;

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	gap: 2px;
	min-height: 40px;
	padding: 4px 8px;
	background-color: #fafafa;
	border-bottom: 1px solid #d9d9d9;
`;

const ToolbarButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	font-family: inherit;
	font-size: 16px;
	color: #2e333e;
	background: transparent;
	border: 0;
	border-radius: 4px;
	cursor: pointer;

	&:hover:not(:disabled),
	&:focus-visible {
		color: #1677ff;
		background-color: #e6f4ff;
	}

	&:focus-visible {
		outline: 2px solid #91caff;
		outline-offset: 1px;
	}

	&:disabled {
		color: #bfbfbf;
		cursor: not-allowed;
	}
`;

const EditableArea = styled.div<{ $backgroundColor?: string }>`
	flex: 1 1 auto;
	min-height: 0;
	padding: 12px;
	overflow-y: auto;
	font-family: inherit;
	font-size: 14px;
	line-height: 1.6;
	color: #2e333e;
	background-color: ${(props) => props.$backgroundColor || "#424242"};
	outline: none;
	white-space: pre-wrap;
	word-break: break-word;

	a {
		color: #1677ff;
		text-decoration: underline;
	}

	/* The global reset leaves lists without an indent, which puts their markers outside the box. */
	ul,
	ol {
		margin: 0;
		padding-left: 24px;
	}

	&[data-placeholder][data-is-empty="true"]::before {
		content: attr(data-placeholder);
		color: #bfbfbf;
		pointer-events: none;
	}
`;

const HiddenFileInput = styled.input`
	display: none;
`;

// Lists have no toolbar button, but a stored text may hold them (the KVKK texts do); they are kept, as the backend and the
// site keep them, instead of being unwrapped into one line.
const ALLOWED_TAGS = new Set([
	"A",
	"B",
	"BR",
	"DIV",
	"EM",
	"I",
	"LI",
	"OL",
	"P",
	"STRONG",
	"U",
	"UL",
]);
const DROP_WITH_CONTENT_TAGS = new Set([
	"EMBED",
	"IFRAME",
	"MATH",
	"OBJECT",
	"SCRIPT",
	"STYLE",
	"SVG",
	"TEMPLATE",
]);
const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);

const getSafeHref = (href: string) => {
	const trimmedHref = href.trim();
	if (!trimmedHref) {
		return null;
	}

	try {
		const origin =
			window.location.origin === "null"
				? "https://localhost"
				: window.location.origin;
		const parsedUrl = new URL(trimmedHref, origin);
		return SAFE_LINK_PROTOCOLS.has(parsedUrl.protocol) ? trimmedHref : null;
	} catch {
		return null;
	}
};

const unwrapElement = (element: Element) => {
	const parent = element.parentNode;
	if (!parent) {
		return;
	}

	while (element.firstChild) {
		parent.insertBefore(element.firstChild, element);
	}
	element.remove();
};

const replaceUnderlineSpan = (element: HTMLElement) => {
	const underline = document.createElement("u");
	while (element.firstChild) {
		underline.appendChild(element.firstChild);
	}
	element.replaceWith(underline);
};

const sanitizeNode = (node: Node) => {
	if (node.nodeType === Node.TEXT_NODE) {
		return;
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		(node as ChildNode).remove();
		return;
	}

	const element = node as HTMLElement;
	const tagName = element.tagName.toUpperCase();
	if (DROP_WITH_CONTENT_TAGS.has(tagName)) {
		element.remove();
		return;
	}

	Array.from(element.childNodes).forEach(sanitizeNode);

	if (tagName === "SPAN") {
		const preservesUnderline =
			(element.style.textDecoration || "").includes("underline") ||
			(element.style.textDecorationLine || "").includes("underline");
		if (preservesUnderline) {
			replaceUnderlineSpan(element);
		} else {
			unwrapElement(element);
		}
		return;
	}

	if (!ALLOWED_TAGS.has(tagName)) {
		unwrapElement(element);
		return;
	}

	const href = tagName === "A" ? element.getAttribute("href") : null;
	const target = tagName === "A" ? element.getAttribute("target") : null;
	Array.from(element.attributes).forEach((attribute) =>
		element.removeAttribute(attribute.name),
	);

	if (tagName !== "A") {
		return;
	}

	const safeHref = href ? getSafeHref(href) : null;
	if (!safeHref) {
		unwrapElement(element);
		return;
	}

	element.setAttribute("href", safeHref);
	if (target === "_blank") {
		element.setAttribute("target", "_blank");
		element.setAttribute("rel", "noopener noreferrer");
	}
};

// The value as the editor keeps it: a stored text that was written elsewhere comes back from the editor in this form
// without being edited, so forms compare it with this, not with the stored text itself.
export const sanitizeRichTextHtml = (html: string) => {
	const template = document.createElement("template");
	template.innerHTML = html;
	Array.from(template.content.childNodes).forEach(sanitizeNode);

	const visibleText = (template.content.textContent ?? "")
		.replace(/[\u00a0\u200b-\u200d\u2060\ufeff]/gi, " ")
		.trim();

	return visibleText ? template.innerHTML : "";
};

const normalizeUrl = (value: string) => {
	const trimmedValue = value.trim();
	if (!trimmedValue) {
		return null;
	}

	if (/^(https?:\/\/|mailto:|tel:|\/)/i.test(trimmedValue)) {
		return trimmedValue;
	}

	if (/^[a-z][a-z\d+.-]*:/i.test(trimmedValue)) {
		return null;
	}

	return `https://${trimmedValue}`;
};

const RichTextComponent = ({
	messageTemplate,
	handleSetChangedTemplate,
	placeholder,
	backgroundColor,
	height = TEXT_EDITOR_HEIGHT,
	resizable = true,
	maxLength,
	lineBreaks = "lf",
}: RichTextComponentProps) => {
	const editorRef = useRef<HTMLDivElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const selectionRef = useRef<Range | null>(null);
	const handleSetChangedTemplateRef = useRef(handleSetChangedTemplate);
	const [isUploading, setIsUploading] = useState(false);
	const [isEditorEmpty, setIsEditorEmpty] = useState(() => !sanitizeRichTextHtml(messageTemplate ?? ""));
	// The value as the form keeps it (the effect below hands the sanitized HTML back), for the character count.
	const [storedValue, setStoredValue] = useState(() => sanitizeRichTextHtml(messageTemplate ?? ""));
	const countId = useId();

	const emitContent = () => {
		if (!editorRef.current) {
			return;
		}

		const content = editorRef.current.innerHTML;
		setStoredValue(sanitizeRichTextHtml(content));
		handleSetChangedTemplateRef.current(content);
	};

	const saveSelection = () => {
		const editor = editorRef.current;
		const selection = window.getSelection();

		if (!editor || !selection || selection.rangeCount === 0) {
			return;
		}

		const range = selection.getRangeAt(0);
		if (editor.contains(range.commonAncestorContainer)) {
			selectionRef.current = range.cloneRange();
		}
	};

	const restoreSelection = () => {
		const selection = window.getSelection();
		if (!selection || !selectionRef.current) {
			return;
		}

		selection.removeAllRanges();
		selection.addRange(selectionRef.current);
	};

	const runCommand = (command: FormatCommand) => {
		editorRef.current?.focus();
		restoreSelection();
		document.execCommand(command, false);
		saveSelection();
		emitContent();
	};

	const handleToolbarMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		saveSelection();
	};

	const createLink = (url: string, text?: string) => {
		const editor = editorRef.current;
		const safeUrl = getSafeHref(url);
		if (!editor || !safeUrl) {
			return;
		}

		editor.focus();
		restoreSelection();

		const selection = window.getSelection();
		if (text || !selection || selection.isCollapsed) {
			const link = document.createElement("a");
			link.href = safeUrl;
			link.target = "_blank";
			link.rel = "noopener noreferrer";
			link.textContent = text || safeUrl;

			const range = selection?.rangeCount
				? selection.getRangeAt(0)
				: null;
			if (range && editor.contains(range.commonAncestorContainer)) {
				range.deleteContents();
				range.insertNode(link);
				range.setStartAfter(link);
				range.collapse(true);
				selection?.removeAllRanges();
				selection?.addRange(range);
			} else {
				editor.appendChild(link);
			}
		} else {
			document.execCommand("createLink", false, safeUrl);
			const anchor = selection.anchorNode?.parentElement?.closest("a");
			if (anchor && editor.contains(anchor)) {
				anchor.target = "_blank";
				anchor.rel = "noopener noreferrer";
			}
		}

		saveSelection();
		emitContent();
	};

	const handleLinkClick = () => {
		const enteredUrl = window.prompt("Bağlantı adresini girin");
		if (enteredUrl === null) {
			return;
		}

		const url = normalizeUrl(enteredUrl);
		if (!url) {
			window.alert("Geçerli bir bağlantı adresi girin.");
			return;
		}

		createLink(url);
	};

	const handleFileClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}

		const formData = new FormData();
		formData.append("file", file);
		setIsUploading(true);

		try {
			const response = await axiosInstance.post(
				NEWS_API_ENDPOINTS.ADD_NEWS_FILE,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);

			const fileUrl = response.data.result.item;
			createLink(fileUrl, file.name);
		} catch (error) {
			console.error("Error uploading the file:", error);
		} finally {
			setIsUploading(false);
			event.target.value = "";
		}
	};

	const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		const text = event.clipboardData.getData("text/plain");
		document.execCommand("insertText", false, text);
		saveSelection();
		emitContent();
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
	};

	useEffect(() => {
		handleSetChangedTemplateRef.current = handleSetChangedTemplate;
	}, [handleSetChangedTemplate]);

	useEffect(() => {
		const nextValue = sanitizeRichTextHtml(messageTemplate ?? "");
		if (editorRef.current && editorRef.current.innerHTML !== nextValue) {
			editorRef.current.innerHTML = nextValue;
		}
		setIsEditorEmpty(!nextValue);
		setStoredValue(nextValue);
		handleSetChangedTemplateRef.current(nextValue);
	}, [messageTemplate]);

	const characterCount = countText(storedValue.trim(), lineBreaks);
	const isExceeded = maxLength !== undefined && characterCount > maxLength;

	const editor = (
		<EditorContainer
			$backgroundColor={backgroundColor}
			$height={height}
			$resizable={resizable}
			$exceeded={isExceeded}
		>
			<Toolbar role="toolbar" aria-label="Metin biçimlendirme araçları">
				<ToolbarButton
					type="button"
					title="Kalın"
					aria-label="Kalın"
					onMouseDown={handleToolbarMouseDown}
					onClick={() => runCommand("bold")}
				>
					<BoldOutlined />
				</ToolbarButton>
				<ToolbarButton
					type="button"
					title="İtalik"
					aria-label="İtalik"
					onMouseDown={handleToolbarMouseDown}
					onClick={() => runCommand("italic")}
				>
					<ItalicOutlined />
				</ToolbarButton>
				<ToolbarButton
					type="button"
					title="Altı çizili"
					aria-label="Altı çizili"
					onMouseDown={handleToolbarMouseDown}
					onClick={() => runCommand("underline")}
				>
					<UnderlineOutlined />
				</ToolbarButton>
				<ToolbarButton
					type="button"
					title="Bağlantı ekle"
					aria-label="Bağlantı ekle"
					onMouseDown={handleToolbarMouseDown}
					onClick={handleLinkClick}
				>
					<LinkOutlined />
				</ToolbarButton>
				<ToolbarButton
					type="button"
					title="Dosya bağlantısı ekle"
					aria-label="Dosya bağlantısı ekle"
					disabled={isUploading}
					onMouseDown={handleToolbarMouseDown}
					onClick={handleFileClick}
				>
					<PaperClipOutlined spin={isUploading} />
				</ToolbarButton>
				<HiddenFileInput
					ref={fileInputRef}
					type="file"
					onChange={handleFileChange}
				/>
			</Toolbar>
			<EditableArea
				ref={editorRef}
				contentEditable
				suppressContentEditableWarning
				role="textbox"
				aria-label="Zengin metin alanı"
				aria-multiline="true"
				data-testid="rich-text-editor"
				data-placeholder={placeholder}
				data-is-empty={isEditorEmpty ? "true" : "false"}
				aria-describedby={maxLength === undefined ? undefined : countId}
				aria-invalid={isExceeded || undefined}
				$backgroundColor={backgroundColor}
				onInput={emitContent}
				onKeyUp={saveSelection}
				onMouseUp={saveSelection}
				onBlur={saveSelection}
				onPaste={handlePaste}
				onDrop={handleDrop}
			/>
		</EditorContainer>
	);

	if (maxLength === undefined) {
		return editor;
	}

	return (
		<>
			{editor}
			<CharacterCount id={countId} $exceeded={isExceeded} data-testid="rich-text-count">
				Biçimlendirme dahil {characterCount} / {maxLength}
			</CharacterCount>
		</>
	);
};

export default RichTextComponent;
